import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Table,
  OverlayTrigger,
  Popover,
  Modal,
} from "react-bootstrap";
import { AiOutlineFilePdf } from "react-icons/ai";
import { RotatingLines } from "react-loader-spinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import url from "../../../configs/config";
import Card from "../../../components/Card";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import { IoEyeOutline } from "react-icons/io5";
import Select from "react-select";
import FadeLoaderComponent from "../../../components/loader-component";
import SearchInput from "../../../components/search-input";
import PaginationComponent from "../../../components/pagination/pagination-component";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { FiMinusCircle } from "react-icons/fi";
import { roleTypes } from "../../../data/roleType";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";

const colors = [
  {
    status: "active",
    color: "bg-success",
  },
  {
    status: "in-active",
    color: "bg-warning",
  },
  {
    status: "closed",
    color: "bg-primary",
  },
  {
    status: "pending",
    color: "bg-info",
  },
];

const JobList = () => {
  const alertRef = useRef(null);
  const [successStatus, setSuccessStatus] = useState({});
  const [failStatus, setFailStatus] = useState({});
  const [blockIsSubmit, setBlockIsSubmit] = useState(true);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteIsSubmit, setDeleteIsSubmit] = useState(true);
  const [dataLoader, setDataLoader] = useState(true);
  const [selectedData, setSelectedData] = useState();
  const [decodedToken, setDecodedToken] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const apiCallInProgress = useRef(false);
  const initialLoadComplete = useRef(false);
  const navigate = useNavigate();
  const [statusIsSubmit, setStatusIsSubmit] = useState(true);
  const token = localStorage.getItem("coyote-token");
  const [jobStatusId, setJobStatusId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const permissions = useSelector(SettingSelector.set_permissions);
  const hasViewPermission = permissions?.job?.view === true;
  const hasUpdatePermission = permissions?.job?.update === true;
  const hasDeletePermission = permissions?.job?.delete === true;
  const hasCreatePermission = permissions?.job?.create === true;
  const hasStatusPermission = permissions?.job?.status === true;
  const hasDownloadReportsPermission =
    permissions?.job?.download_reports === true;
  const hasViewProductReportsPermission =
    permissions?.job?.view_job_product_report === true;
  const hasUdateJobItemStatusPermission =
    permissions?.job?.update_job_item_status === true;

  const hasBomCreatePermission = permissions?.job?.create_bom === true;
  const hasBomViewPermission = permissions?.job?.view_bom === true;
  const hasBomEditPermission = permissions?.job?.edit_bom === true;
  const hasBomDeletePermission = permissions?.job?.delete_bom === true;
  const hasBomCreatePRPermission = permissions?.job?.create_bom_pr === true;
  const hasBomPrintPermission = permissions?.job?.print_bom === true;

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  const jobStatusOptions = ["pending", "in-progress", "completed"];
  const jobStatusOptionsFormatted = jobStatusOptions.map((status) => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1),
  }));

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getJobs = async (offset = 0, limit = 10) => {
    if (apiCallInProgress.current) return;
    apiCallInProgress.current = true;
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      setDataLoader(false);
      return;
    } else {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
        params: {
          offset,
          limit,
          search:
            searchQuery.trim().length >= 2
              ? searchQuery.trim()
              : searchQuery.trim().length < 1
                ? null
                : null,
        },
      };

      try {
        const response = await axios.get(`${url.backendURL}job/`, config);
        setJobs(response.data.response.data);
        setTotalEntries(response.data.response.jobsCount);
        setDataLoader(false);
      } catch (err) {
        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        handleError(err, "jobs");

        setDataLoader(false);
      } finally {
        apiCallInProgress.current = false;
        initialLoadComplete.current = true;
      }
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        },
      );
    } else {
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  useEffect(() => {
    if (!decodedToken) return;
    if (!initialLoadComplete.current) {
      getJobs((currentPage - 1) * entriesPerPage, entriesPerPage);
    } else if (!apiCallInProgress.current) {
      getJobs((currentPage - 1) * entriesPerPage, entriesPerPage);
    }
  }, [currentPage, entriesPerPage, decodedToken]);

  useEffect(() => {
    if (!initialLoadComplete.current) return;
    const debounceTimer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2 || searchQuery.trim().length < 1) {
        setLoading(true);
        setCurrentPage(1);
        await getJobs(0, entriesPerPage);
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleDeleteJob = async (e, id) => {
    e.preventDefault();
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const response = await axios.delete(`${url.backendURL}job/${id}`, config);
      await getJobs((currentPage - 1) * entriesPerPage, entriesPerPage);

      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "jobs",
      });
      setShowPopover(null);
      setDeleteIsSubmit(true);

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } catch (err) {
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "jobs");
      setDeleteIsSubmit(true);
      setShowPopover(null);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (totalEntries === 0) return;
    setCurrentPage(pageNumber);
  };

  const handleEntriesPerPageChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const [showPopover, setShowPopover] = useState(null);

  const togglePopover = (idx) => {
    if (showPopover === idx) {
      setShowPopover(null);
    } else {
      setShowPopover(idx);
    }
  };

  useEffect(() => {
    function toggleStickyNames() {
      const isWideScreen = window.innerWidth > 650;

      const stickyHeadNames = document.querySelectorAll(".sticky-head-name");
      const stickyDataNames = document.querySelectorAll(".sticky-data-names");

      if (isWideScreen) {
        stickyHeadNames.forEach((head) => {
          head.classList.add("sticky-action-head-buttons");
          const parentRow = head.closest(".tr-user");
          if (parentRow) {
            head.style.height = `${parentRow.offsetHeight}px`;
          }
        });
        stickyDataNames.forEach((data) => {
          data.classList.add("sticky-action-buttons");
          const parentRow = data.closest(".tr-user");
          if (parentRow) {
            data.style.height = `${parentRow.offsetHeight}px`;
          }
        });
      } else {
        stickyHeadNames.forEach((head) => {
          head.classList.remove("sticky-action-head-buttons");
          head.style.height = "auto";
        });
        stickyDataNames.forEach((data) => {
          data.classList.remove("sticky-action-buttons");
          data.style.height = "auto";
        });
      }
    }
    toggleStickyNames();
    window.addEventListener("resize", toggleStickyNames);
    return () => window.removeEventListener("resize", toggleStickyNames);
  }, [dataLoader, jobs]);

  useEffect(() => {
    const handleScroll = () => {
      if (showPopover !== null) {
        setShowPopover(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showPopover]);

  const getColorByStatus = (status) => {
    const colorObject = colors.find((color) => color.status === status);
    return colorObject ? colorObject.color : "";
  };
  const [htmlContent, setHtmlContent] = useState("");

  useEffect(() => {
    fetch("/report.html")
      .then((res) => res.text())
      .then((data) => {
        setHtmlContent(data);
      })
      .catch((err) => {
        console.error("Error loading HTML file:", err);
      });
  }, []);

  const handleJobReportData = (jobData) => {
    const newWindow = window.open("", "_blank");

    if (htmlContent) {
      newWindow.document.write(htmlContent);

      setTimeout(() => {
        if (newWindow.updateJobReport) {
          newWindow.updateJobReport(jobData);
        } else {
          console.error("update job function not found in report.html");
        }
      }, 100);
    }
  };
  const handleJobReport = async (jobId, jobName) => {
    if (!token) {
      localStorage.removeItem("coyote-token");
      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const response = await axios.get(
        `${url.backendURL}job/${jobId}/product-report`,
        config,
      );
      handleJobReportData(response.data.response.data);
    } catch (err) {
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "jobs");
    }
  };

  const handleSaveJobStatusField = async (id, value) => {
    if (!token) {
      localStorage.removeItem("coyote-token");
      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      let data = {
        jobStatus: value,
      };

      await axios.put(`${url.backendURL}job/${id}/job-status`, data, config);

      setIsStatusLoading(false);
      setSuccessStatus({
        field: "job_status",
      });
      setStatusIsSubmit(true);
      setTimeout(() => {
        setSuccessStatus({});
      }, 1000);
    } catch (err) {
      setFailStatus({
        field: "job_status",
      });
      setIsStatusLoading(false);
      setTimeout(() => {
        setFailStatus({});
      }, 1000);
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "jobs");
      setStatusIsSubmit(true);
      throw err;
    }
  };

  const handleConfirmCompleted = () => {
    if (pendingStatusChange) {
      const { option, jobId } = pendingStatusChange;

      handleSaveJobStatusField(jobId, option.value)
        .then(() => {
          setSuccessStatus({ field: "job_status" });
          setJobs((prevJobs) =>
            prevJobs.map((job) =>
              job.id === jobId
                ? {
                    ...job,
                    job_status: option.value,
                    status:
                      option.value === "completed" ? "closed" : job.status,
                  }
                : job,
            ),
          );

          if (alertRef.current) {
            alertRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
          setAlert({
            message: `Job status updated to ${option.label} successfully`,
            type: "success",
            show: true,
            alertFor: "jobs",
          });
          setTimeout(() => {
            setAlert({ show: false, alertFor: "", message: "", type: "" });
          }, 5000);
          setIsStatusLoading(false);
        })
        .catch(() => {
          setFailStatus({ field: "job_status" });
          setIsStatusLoading(false);
        })
        .finally(() => {
          setShowConfirmModal(false);
          setPendingStatusChange(null);
        });
    }
  };

  const handleCancelCompleted = () => {
    setShowConfirmModal(false);
    setPendingStatusChange(null);
    setIsStatusLoading(false);
  };

  const handleSubmitJobStatus = (option, jobId) => {
    if (option.value === "completed") {
      setPendingStatusChange({ option, jobId });
      setShowConfirmModal(true);
    } else {
      handleSaveJobStatusField(jobId, option.value)
        .then(() => {
          setSuccessStatus({ field: "job_status" });
          setJobs((prevJobs) =>
            prevJobs.map((job) =>
              job.id === jobId
                ? {
                    ...job,
                    job_status: option.value,
                    status:
                      option.value === "completed" ? "closed" : job.status,
                  }
                : job,
            ),
          );

          if (alertRef.current) {
            alertRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
          setAlert({
            message: `Job status updated to ${option.label} successfully`,
            type: "success",
            show: true,
            alertFor: "jobs",
          });
          setTimeout(() => {
            setAlert({ show: false, alertFor: "", message: "", type: "" });
          }, 5000);
          setIsStatusLoading(false);
        })
        .catch(() => {
          setFailStatus({ field: "job_status" });
          setIsStatusLoading(false);
        });
    }
  };

  const isJobStatusTransitionAllowed = (
    currentJobStatus,
    newJobStatus,
    itemStatus,
  ) => {
    if (itemStatus === "in-active") {
      return false;
    }

    switch (currentJobStatus) {
      case "pending":
        return newJobStatus === "in-progress" || newJobStatus === "completed";

      case "in-progress":
        return newJobStatus === "completed" || newJobStatus === "pending";

      default:
        return true;
    }
  };

  const handleJobStatus = async (e, item) => {
    e.preventDefault();
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }
    const jobStatus = item?.status === "in-active" ? "active" : "in-active";

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const data = {
        status: jobStatus,
      };
      const response = await axios.put(
        `${url.backendURL}job/${item?.id}/status`,
        data,
        config,
      );
      await getJobs((currentPage - 1) * entriesPerPage, entriesPerPage);

      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "jobs",
      });

      setBlockIsSubmit(true);

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } catch (err) {
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "jobs");
      setBlockIsSubmit(true);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return amount;
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "N/A";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const getJobStatusOptionsWithDisabled = (currentJobStatus, itemStatus) => {
    if (itemStatus === "in-active") {
      return jobStatusOptionsFormatted.map((option) => ({
        ...option,
        isDisabled: true,
      }));
    }

    return jobStatusOptionsFormatted.map((option) => ({
      ...option,
      isDisabled: !isJobStatusTransitionAllowed(
        currentJobStatus,
        option.value,
        itemStatus,
      ),
    }));
  };

  return (
    <>
      <style>
        {`
          .table-user .sticky-action-buttons {
            background-color: #ffffff !important;
            z-index: 2;
          }
          .table-user .tr-user:hover .sticky-action-buttons {
            background-color: #f5f6fa !important;
          }
          .table-user .sticky-action-head-buttons {
            background: #e2e4e9 !important;
          }
          .table-user .sticky-action-head-buttons,
          .table-user .sticky-action-buttons {
            left: 0;
            z-index: 11;
            position: absolute;
            width: 260px;
            height: inherit;
            display: flex;
            align-items: center;
            padding-left: 15px;
            border-bottom: 1px solid #ebedef !important;
          }
          .table-user .sticky-action-buttons {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .table-user .th-user:nth-child(2),
          .table .td-user:nth-child(2) {
            padding-left: 275px !important;
          }
           @media (max-width: 650px) {
            .table-user .sticky-action-head-buttons,
            .table-user .sticky-action-buttons {
               position: static !important;
               width: auto !important;
               white-space: normal !important;
            }
            .table-user .th-user:nth-child(2),
            .table .td-user:nth-child(2) {
              padding-left: 0.75rem !important;
            }
          }
        `}
      </style>
      <div>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header className="d-flex justify-content-between listing-card-header">
                <div className="header-title">
                  <h4 className="card-title">Job List</h4>
                </div>
              </Card.Header>

              <div className="d-flex align-items-center justify-content-between">
                <Row className="align-items-center d-flex justify-content-between mt-2 w-100">
                  {/* Show entries section */}
                  <Col
                    sm="5"
                    md="4"
                    lg="4"
                    xs="12"
                    ref={alertRef}
                    className="order-1"
                  >
                    <div className="pt-4 d-flex align-items-center col-12 ms-4 listing-entries">
                      <label className="me-2">Show</label>
                      <select
                        className="form-select py-1 ps-2 pe-0 text-sm bg-white rounded-lg w-25"
                        value={entriesPerPage}
                        onChange={handleEntriesPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                      <span className="ms-2">entries</span>
                    </div>
                  </Col>

                  <Col sm="7" md="8" lg="8" xs="12" className="order-2">
                    <div className="pt-4 d-flex justify-content-start align-items-center">
                      <Row className="g-2 ms-4 ms-sm-0 align-items-end w-100 justify-content-end listing-controls-row">
                        {/* Search Input */}
                        <Col
                          xs="12"
                          sm="auto"
                          md="auto"
                          lg="auto"
                          className="d-flex ps-0 me-0 me-sm-0 flex-column flex-md-row align-items-start align-items-md-center listing-search-col"
                        >
                          <label className="me-2 mb-1 mb-md-0 text-nowrap">
                            Search:
                          </label>
                          <div
                            style={{ maxWidth: "180px", width: "100%" }}
                            className="listing-search-input-wrapper pe-sm-0 pe-3"
                          >
                            <SearchInput
                              searchQuery={searchQuery}
                              setSearchQuery={setSearchQuery}
                              loading={loading}
                              setLoading={setLoading}
                            />
                          </div>
                        </Col>
                        {(decodedToken?.roles?.some(
                          (role) => role.role_type === roleTypes.SUPER_ADMIN,
                        ) ||
                          hasCreatePermission) && (
                          <Col
                            xs="12"
                            sm="auto"
                            md="auto"
                            lg="auto"
                            className="d-flex justify-content-start justify-content-md-end align-items-center ps-0 ps-sm-4 listing-add-button-col"
                          >
                            <Link
                              to={`/dashboard/app/job-add`}
                              className="mt-1 mt-md-0"
                            >
                              <Button
                                type="submit"
                                variant="btn btn-primary"
                                className=" responsive-button "
                              >
                                Add Job
                              </Button>
                            </Link>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                </Row>
              </div>

              <Card.Body className="px-0">
                {alert.alertFor === "jobs" && (
                  <Row>
                    <Col className="mt-2 mb-2 ms-4 me-4">
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                    </Col>{" "}
                  </Row>
                )}

                <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table
                    id="job-list-table datatable"
                    className="table-user"
                    role="grid"
                    responsive
                    data-toggle="data-table"
                    style={{ minWidth: "1500px" }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "#e2e4e9" }}
                        className="tr-user"
                      >
                        <th className="sticky-head-name th-user">
                          Project Name
                        </th>
                        <th className="th-user" style={{ minWidth: "200px" }}>
                          Site Name
                        </th>
                        <th className="th-user" style={{ minWidth: "180px" }}>
                          Estimate
                        </th>
                        <th className="th-user" style={{ minWidth: "150px" }}>
                          Start Date
                        </th>
                        <th className="th-user" style={{ minWidth: "180px" }}>
                          System
                        </th>
                        <th className="th-user" style={{ minWidth: "180px" }}>
                          Phase
                        </th>
                        <th className="th-user" style={{ minWidth: "180px" }}>
                          Stakeholders
                        </th>
                        <th className="th-user" style={{ minWidth: "250px" }}>
                          Job Status
                        </th>
                        <th className="th-user" style={{ minWidth: "150px" }}>
                          Status
                        </th>
                        <th className="th-user" style={{ minWidth: "150px" }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    {!dataLoader && jobs.length > 0 ? (
                      <tbody>
                        {jobs?.map((item, idx) => (
                          <tr
                            key={idx}
                            className="table-hover tr-user"
                            onDoubleClick={() => {
                              if (
                                hasViewPermission ||
                                hasUpdatePermission ||
                                hasViewProductReportsPermission ||
                                hasUdateJobItemStatusPermission ||
                                hasBomCreatePermission ||
                                hasBomViewPermission ||
                                hasBomEditPermission ||
                                hasBomDeletePermission ||
                                hasBomCreatePRPermission ||
                                hasBomPrintPermission ||
                                decodedToken?.roles.some(
                                  (role) =>
                                    role.role_type === roleTypes.SUPER_ADMIN,
                                )
                              ) {
                                navigate(`/dashboard/app/job-edit/${item.id}`);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                             <td className="sticky-data-names td-user">
                               {item.name}
                            </td>
                            <td
                              className="description-cell td-user"
                              style={{ textWrap: "revert" }}
                            >
                              {item.site_name && item.site_name.length > 50
                                ? `${item.site_name.substring(0, 50)}...`
                                : item.site_name}
                            </td>
                            <td className="td-user">
                              {formatCurrency(item?.estimate)}
                            </td>
                            <td className="td-user">
                              {new Date(item.bid_date).toLocaleDateString(
                                "en-US",
                                {
                                  month: "2-digit",
                                  day: "2-digit",
                                  year: "numeric",
                                },
                              )}
                            </td>
                            <td className="td-user" title={item?.system_name}>
                              {truncateText(item?.system_name, 25)}
                            </td>
                            <td className="td-user" title={item?.phase_name}>
                              {truncateText(item?.phase_name, 25)}
                            </td>

                            <td className="td-user">
                              {item?.stakeholder_contacts &&
                              item.stakeholder_contacts.length > 0 ? (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Popover id={`popover-stakeholder-${idx}`}>
                                      <Popover.Header as="h3">
                                        <strong>Stakeholder Contacts</strong>
                                      </Popover.Header>
                                      <Popover.Body>
                                        {item.stakeholder_contacts.map(
                                          (sc, scIdx) => (
                                            <div key={scIdx} className="mb-2">
                                              <strong>
                                                {sc.stakeholder_name}
                                              </strong>
                                              <br />
                                              <span>{sc.type}</span>
                                              {sc.contact_title && (
                                                <>
                                                  <br />
                                                  <small>
                                                    Title: {sc.contact_title}
                                                  </small>
                                                </>
                                              )}
                                              {sc.contact_name && (
                                                <>
                                                  <br />
                                                  <small>
                                                    Name: {sc.contact_name}
                                                  </small>
                                                </>
                                              )}
                                              {sc.contact_email && (
                                                <>
                                                  <br />
                                                  <small>
                                                    Email: {sc.contact_email}
                                                  </small>
                                                </>
                                              )}
                                              {sc.contact_phone && (
                                                <>
                                                  <br />
                                                  <small>
                                                    Phone: {sc.contact_phone}
                                                  </small>
                                                </>
                                              )}
                                              {scIdx <
                                                item.stakeholder_contacts
                                                  .length -
                                                  1 && <hr />}
                                            </div>
                                          ),
                                        )}
                                      </Popover.Body>
                                    </Popover>
                                  }
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      overflowX: "auto",
                                      whiteSpace: "nowrap",
                                      flexDirection: "column",
                                      gap: "4px",
                                      cursor: "pointer",
                                      scrollbarWidth: "none",
                                      msOverflowStyle: "none",
                                      maxWidth: "200px",
                                    }}
                                    className="no-scrollbar"
                                  >
                                    {[
                                      ...new Set(
                                        item.stakeholder_contacts
                                          .filter((sc) => sc.stakeholder_name)
                                          .map((sc) => sc.stakeholder_name),
                                      ),
                                    ].map((name, index) => (
                                      <span key={index}>{name}</span>
                                    ))}
                                  </div>
                                </OverlayTrigger>
                              ) : (
                                "N/A"
                              )}
                            </td>

                            <td className="text-capitalize td-user">
                              <div
                                title={item?.job_status}
                                style={{
                                  position: "relative",
                                  display: "inline-block",
                                  width: "100%",
                                }}
                              >
                                <Select
                                  closeMenuOnScroll={(e) => {
                                    try {
                                      const target = e.target;

                                      if (
                                        !target ||
                                        !target.nodeType ||
                                        typeof target.closest !== "function"
                                      ) {
                                        return true;
                                      }

                                      const isMenuScroll =
                                        target.closest('[class*="MenuList"]') ||
                                        target.closest('[class*="menu"]');

                                      return !isMenuScroll;
                                    } catch (error) {
                                      return true;
                                    }
                                  }}
                                  name="job_status"
                                  options={getJobStatusOptionsWithDisabled(
                                    item?.job_status,
                                    item?.status,
                                  )}
                                  value={jobStatusOptionsFormatted?.find(
                                    (option) =>
                                      option.value === item?.job_status,
                                  )}
                                  isDisabled={
                                    item?.job_status === "completed" ||
                                    item?.status === "in-active" ||
                                    (!hasUpdatePermission &&
                                      decodedToken?.roles?.some(
                                        (role) =>
                                          role.role_type !==
                                          roleTypes.SUPER_ADMIN,
                                      ))
                                  }
                                  onChange={(e) => {
                                    if (e?.value === item?.job_status) {
                                      return;
                                    } else {
                                      setSelectedJob(item);
                                      setIsStatusLoading(true);
                                      setJobStatusId(item?.id);
                                      handleSubmitJobStatus(e, item?.id);
                                    }
                                  }}
                                  isClearable={false}
                                  placeholder="Select Status"
                                  isOptionDisabled={(option) =>
                                    option.isDisabled
                                  }
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  menuPlacement="auto"
                                  styles={{
                                    container: (provided) => ({
                                      ...provided,
                                      minWidth: "150px",
                                    }),
                                    control: (provided, state) => ({
                                      ...provided,
                                      backgroundColor: "#fff",
                                      borderColor:
                                        successStatus?.field === "job_status" &&
                                        jobStatusId === item?.id
                                          ? "green"
                                          : failStatus?.field ===
                                                "job_status" &&
                                              jobStatusId === item?.id
                                            ? "red"
                                            : "#ced4da",
                                      boxShadow:
                                        successStatus?.field === "job_status" &&
                                        jobStatusId === item?.id
                                          ? "0 0 5px green"
                                          : failStatus.field === "job_status" &&
                                              jobStatusId === item?.id
                                            ? "0 0 5px red"
                                            : "none",
                                    }),
                                    option: (provided, state) => ({
                                      ...provided,
                                      backgroundColor: state.isSelected
                                        ? "#007bff"
                                        : state.isFocused && !state.isDisabled
                                          ? "#e6f7ff"
                                          : state.isDisabled
                                            ? "#f8f9fa"
                                            : "#fff",

                                      cursor: state.isDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                    }),
                                    menuPortal: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,
                                    }),
                                    menu: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />

                                {isStatusLoading &&
                                  jobStatusId === item?.id && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "40%",
                                        right: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                      }}
                                    >
                                      <RotatingLines
                                        strokeWidth="5"
                                        animationDuration="0.75"
                                        width="20"
                                        strokeColor="#b2b4b9"
                                        color="#b2b4b9"
                                        visible={true}
                                      />
                                    </div>
                                  )}
                              </div>
                            </td>

                            <td className="">
                              <span
                                className={`badge p-1 px-2 ${getColorByStatus(
                                  item.status,
                                )}`}
                              >
                                {item.status}
                              </span>
                            </td>

                            <td
                              style={{ textWrap: "revert" }}
                              className="td-user"
                            >
                              <div className="d-flex align-items-center justify-content-end list-user-action">
                                {(hasDownloadReportsPermission ||
                                  decodedToken?.roles.some(
                                    (role) =>
                                      role.role_type === roleTypes.SUPER_ADMIN,
                                  )) && (
                                  <Link
                                    className="btn btn-sm btn-icon btn-primary mx-1"
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    title="Job Product Report"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleJobReport(item.id, item.name);
                                    }}
                                  >
                                    <span className="btn-inner">
                                      <AiOutlineFilePdf
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                    </span>
                                  </Link>
                                )}
                                {(hasStatusPermission ||
                                  decodedToken?.roles.some(
                                    (role) =>
                                      role.role_type === roleTypes.SUPER_ADMIN,
                                  )) && (
                                  <>
                                    {" "}
                                    <Link
                                      className={`btn btn-sm btn-icon mx-1 ${
                                        item?.status === "in-active"
                                          ? "btn-success"
                                          : "btn-danger"
                                      } ${
                                        !(
                                          item?.status === "active" ||
                                          item?.status === "in-active"
                                        ) || item?.job_status === "complted"
                                          ? "disabled"
                                          : ""
                                      }`}
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (
                                          (item?.status === "active" ||
                                            item?.status === "in-active") &&
                                          blockIsSubmit &&
                                          item?.job_status !== "completed"
                                        ) {
                                          setBlockIsSubmit(false);
                                          handleJobStatus(e, item);
                                        }
                                      }}
                                      title={
                                        item?.job_status === "completed"
                                          ? "Cannot change status - Job is completed"
                                          : item?.status === "in-active"
                                            ? "Active"
                                            : "In-active"
                                      }
                                      style={{
                                        pointerEvents:
                                          !(
                                            item?.status === "active" ||
                                            item?.status === "in-active"
                                          ) || item?.job_status === "completed"
                                            ? "none"
                                            : "auto",
                                        opacity:
                                          !(
                                            item?.status === "active" ||
                                            item?.status === "in-active"
                                          ) || item?.job_status === "completed"
                                            ? 0.5
                                            : 1,
                                      }}
                                    >
                                      <span className="btn-inner">
                                        {item?.status === "in-active" ? (
                                          <CheckCircleOutlineIcon
                                            style={{
                                              width: "20px",
                                              height: "20px",
                                            }}
                                          />
                                        ) : (
                                          <FiMinusCircle
                                            style={{
                                              width: "20px",
                                              height: "20px",
                                            }}
                                          />
                                        )}
                                      </span>
                                    </Link>{" "}
                                  </>
                                )}
                                {(hasViewPermission ||
                                  hasUpdatePermission ||
                                  hasViewProductReportsPermission ||
                                  hasUdateJobItemStatusPermission ||
                                  hasBomCreatePermission ||
                                  hasBomViewPermission ||
                                  hasBomEditPermission ||
                                  hasBomDeletePermission ||
                                  hasBomCreatePRPermission ||
                                  hasBomPrintPermission ||
                                  decodedToken?.roles.some(
                                    (role) =>
                                      role.role_type === roleTypes.SUPER_ADMIN,
                                  )) && (
                                  <>
                                    {" "}
                                    <Link
                                      className="btn mx-1 btn-sm btn-icon btn-warning"
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      title="Edit"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(
                                          `/dashboard/app/job-edit/${item.id}`,
                                        );
                                      }}
                                    >
                                      <span className="btn-inner">
                                        <svg
                                          width="20"
                                          height="20"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M15.1655 4.60254L19.7315 9.16854"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </span>
                                    </Link>{" "}
                                  </>
                                )}
                                {(hasDeletePermission ||
                                  decodedToken?.roles.some(
                                    (role) =>
                                      role.role_type === roleTypes.SUPER_ADMIN,
                                  )) && (
                                  <>
                                    {" "}
                                    <OverlayTrigger
                                      trigger="click"
                                      show={showPopover === `delete-${idx}`}
                                      onToggle={() =>
                                        togglePopover(`delete-${idx}`)
                                      }
                                      placement="top"
                                      overlay={
                                        <Popover id={`popover-delete-${idx}`}>
                                          <Popover.Header as="h3">
                                            <strong>Delete</strong>{" "}
                                            <span>({item.name})</span>?
                                          </Popover.Header>
                                          <Popover.Body>
                                            Once deleted, this action cannot be
                                            undone.
                                            <div className="d-flex justify-content-end mt-2">
                                              <Button
                                                size="sm"
                                                variant="danger"
                                                className="me-2"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  if (deleteIsSubmit) {
                                                    setDeleteIsSubmit(false);
                                                    handleDeleteJob(e, item.id);
                                                  }
                                                }}
                                              >
                                                Delete
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  togglePopover(
                                                    `delete-${idx}`,
                                                  );
                                                }}
                                              >
                                                Cancel
                                              </Button>
                                            </div>
                                          </Popover.Body>
                                        </Popover>
                                      }
                                    >
                                      <Link
                                        className={`btn mx-1 btn-sm btn-icon btn-danger ${
                                          item?.job_status === "completed"
                                            ? "disabled"
                                            : ""
                                        }`}
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        title={
                                          item?.job_status === "completed"
                                            ? "Cannot delete - Job is completed"
                                            : "Delete"
                                        }
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (
                                            item?.job_status !== "completed"
                                          ) {
                                            setSelectedData({
                                              poNumber: item?.name,
                                              id: item?.id,
                                            });
                                          }
                                        }}
                                        style={{
                                          pointerEvents:
                                            item?.job_status === "completed"
                                              ? "none"
                                              : "auto",
                                          opacity:
                                            item?.job_status === "completed"
                                              ? 0.5
                                              : 1,
                                        }}
                                      >
                                        <span className="btn-inner">
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            stroke="currentColor"
                                          >
                                            <path
                                              d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                            <path
                                              d="M20.708 6.23975H3.75"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                            <path
                                              d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </span>
                                      </Link>
                                    </OverlayTrigger>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    ) : dataLoader ? (
                      <tbody>
                        <tr>
                          <td colSpan={11}>
                            <FadeLoaderComponent />
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <tbody>
                        <tr>
                          <td colSpan={11}>
                            <div className="d-flex w-100 justify-content-center">
                              <p>No job found</p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </Table>
                </div>

                <Row className="align-items-center mt-2">
                  <Col sm="6">
                    <div className="d-flex flex-wrap items-center ms-4">
                      <div className="">
                        Showing{" "}
                        {Math.min(
                          (currentPage - 1) * entriesPerPage + 1,
                          totalEntries,
                        )}{" "}
                        to{" "}
                        {Math.min(currentPage * entriesPerPage, totalEntries)}{" "}
                        of {totalEntries} entries
                      </div>
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="bd-example d-flex justify-content-center justify-content-md-end me-4">
                      <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal
          show={showConfirmModal}
          onHide={handleCancelCompleted}
          backdrop="static"
          keyboard={false}
          centered
        >
          <Modal.Header className="custom-modal-header d-flex relative">
            <Modal.Title>Mark Job as Completed?</Modal.Title>
            <button
              type="button"
              className="btn-close-svg"
              style={{ position: "absolute", right: "1rem" }}
              onClick={() => {
                setStatusIsSubmit(true);

                handleCancelCompleted();
              }}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </Modal.Header>

          <Modal.Body>
            <p>
              Marking this job as <strong>Completed</strong> will set all items
              to <strong>Installed</strong>. Are you sure all products and their
              quantities have been fully installed?
            </p>
          </Modal.Body>

          <Modal.Footer>
            <Button
              onClick={(e) => {
                e.preventDefault();
                if (statusIsSubmit) {
                  setStatusIsSubmit(false);
                  handleConfirmCompleted();
                }
              }}
              variant="btn btn-primary"
            >
              <i
                className="fas fa-check me-2"
                style={{ fontSize: "0.8rem" }}
              ></i>
              Submit
            </Button>
            <Button
              variant="btn btn-danger"
              onClick={() => {
                setStatusIsSubmit(true);
                handleCancelCompleted();
              }}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};
export default JobList;
